/**
 * The two navigation trees.
 *
 * Kept as data rather than JSX so the sidebar, the command palette and the
 * route table can all read the same list — a page that exists in one and not
 * the others is the classic way a section becomes unreachable.
 */

import type { ReactNode } from 'react'
import {
  BagIcon,
  BuildingIcon,
  CardIcon,
  ChartIcon,
  CheckoutIcon,
  GearIcon,
  HomeIcon,
  LedgerIcon,
  MegaphoneIcon,
  SparkIcon,
  PlugIcon,
  ShieldIcon,
  StoreIcon,
  UsersIcon,
  WalletIcon,
} from './icons.js'

export interface NavLeaf {
  label: string
  to: string
  /** Exact match — only for a parent path that is also a page, like `/`. */
  end?: boolean
  badge?: NavBadge
  /** A fixed label beside the item, e.g. `Free`. Unlike `badge`, not a count. */
  tag?: string
}

export type NavBadge = 'orders' | 'abandoned'

export interface NavNode extends NavLeaf {
  icon: ReactNode
  children?: NavLeaf[]
}

export interface NavSection {
  title?: string
  items: NavNode[]
}

export const merchantNav: NavSection[] = [
  {
    items: [
      { label: 'Dashboard', to: '/', end: true, icon: <HomeIcon size={18} /> },
      {
        label: 'Stores',
        to: '/stores',
        icon: <StoreIcon size={18} />,
        children: [
          { label: 'All Stores', to: '/stores', end: true },
          { label: 'Shopify Stores', to: '/stores/shopify' },
          { label: 'WordPress Stores', to: '/stores/wordpress' },
          { label: 'Custom Websites', to: '/stores/custom' },
        ],
      },
      {
        label: 'Orders',
        to: '/orders',
        icon: <BagIcon size={18} />,
        badge: 'abandoned',
        children: [
          { label: 'All Orders', to: '/orders', end: true, badge: 'orders' },
          { label: 'Abandoned Orders', to: '/orders/abandoned', badge: 'abandoned' },
          { label: 'Order Analytics', to: '/orders/analytics' },
        ],
      },
      { label: 'Customers', to: '/customers', icon: <UsersIcon size={18} /> },
    ],
  },
  {
    title: 'Selling',
    items: [
      {
        label: 'Checkout',
        to: '/checkout',
        icon: <CheckoutIcon size={18} />,
        children: [
          { label: 'Buy Now Button', to: '/checkout/buy-now' },
          { label: 'Discounts', to: '/checkout/discounts' },
          { label: 'Shipping', to: '/checkout/shipping' },
          { label: 'COD Rules', to: '/checkout/cod' },
          { label: 'Checkout Settings', to: '/checkout/settings' },
        ],
      },
      {
        label: 'Payments',
        to: '/payments',
        icon: <CardIcon size={18} />,
        children: [
          { label: 'Transactions', to: '/payments/transactions' },
          { label: 'Gateways', to: '/payments/gateways' },
          { label: 'Refunds', to: '/payments/refunds' },
        ],
      },
      {
        label: 'Other Product',
        to: '/shopify-product',
        icon: <SparkIcon size={18} />,
        children: [
          { label: 'Countdown Timer', to: '/shopify-product/countdown', tag: 'Free' },
          { label: 'Frequently Bought Together', to: '/shopify-product/bundles', tag: 'Free' },
        ],
      },
      {
        label: 'Engagement',
        to: '/engagement',
        icon: <MegaphoneIcon size={18} />,
        children: [
          { label: 'Abandoned Checkouts', to: '/engagement/abandoned' },
          { label: 'Notifications', to: '/engagement/notifications' },
        ],
      },
    ],
  },
  {
    title: 'Money',
    items: [
      {
        label: 'Wallet',
        to: '/wallet',
        icon: <WalletIcon size={18} />,
        children: [
          { label: 'Overview', to: '/wallet', end: true },
          { label: 'Transactions', to: '/wallet/transactions' },
          { label: 'Recharge wallet', to: '/wallet/add-money' },
          { label: 'Plans', to: '/wallet/plans' },
          { label: 'Billing & Invoices', to: '/wallet/billing' },
        ],
      },
      {
        label: 'Analytics',
        to: '/analytics',
        icon: <ChartIcon size={18} />,
        children: [
          { label: 'Overview', to: '/analytics', end: true },
          { label: 'Orders', to: '/analytics/orders' },
          { label: 'Revenue', to: '/analytics/revenue' },
          { label: 'Customers', to: '/analytics/customers' },
          { label: 'Conversion', to: '/analytics/conversion' },
          { label: 'Payments', to: '/analytics/payments' },
          { label: 'Abandoned Checkouts', to: '/analytics/abandoned' },
        ],
      },
    ],
  },
  {
    title: 'Setup',
    items: [
      {
        label: 'Integrations',
        to: '/integrations',
        icon: <PlugIcon size={18} />,
        children: [
          { label: 'Shopify', to: '/integrations/shopify' },
          { label: 'WordPress', to: '/integrations/wordpress' },
          { label: 'Custom Website', to: '/integrations/custom' },
          { label: 'API & Webhooks', to: '/integrations/api' },
        ],
      },
      {
        label: 'Settings',
        to: '/settings',
        icon: <GearIcon size={18} />,
        children: [
          { label: 'General', to: '/settings', end: true },
          { label: 'Profile', to: '/settings/profile' },
          { label: 'Team', to: '/settings/team' },
          { label: 'Security', to: '/settings/security' },
          { label: 'Notifications', to: '/settings/notifications' },
        ],
      },
    ],
  },
]

export const adminNav: NavSection[] = [
  {
    items: [{ label: 'Dashboard', to: '/admin', end: true, icon: <HomeIcon size={18} /> }],
  },
  {
    title: 'Manage',
    items: [
      { label: 'Users', to: '/admin/users', icon: <UsersIcon size={18} /> },
      { label: 'Merchants', to: '/admin/merchants', icon: <BuildingIcon size={18} /> },
      { label: 'Stores', to: '/admin/stores', icon: <StoreIcon size={18} /> },
      { label: 'Orders', to: '/admin/orders', icon: <BagIcon size={18} /> },
    ],
  },
  {
    title: 'Money',
    items: [
      {
        label: 'Transactions',
        to: '/admin/ledger',
        icon: <LedgerIcon size={18} />,
        children: [
          { label: 'Ledger', to: '/admin/ledger', end: true },
          { label: 'Recharges', to: '/admin/recharges' },
          { label: 'Commission', to: '/admin/commission' },
          { label: 'Wallet Alerts', to: '/admin/wallet-alerts' },
        ],
      },
      { label: 'Payments', to: '/admin/payments', icon: <CardIcon size={18} /> },
      { label: 'Reports', to: '/admin/analytics', icon: <ChartIcon size={18} /> },
    ],
  },
  {
    title: 'Platform',
    items: [
      { label: 'Integrations', to: '/admin/integrations', icon: <PlugIcon size={18} /> },
      { label: 'WhatsApp', to: '/admin/whatsapp', icon: <MegaphoneIcon size={18} /> },
      { label: 'Health & Audit', to: '/admin/health', icon: <ShieldIcon size={18} /> },
      { label: 'Settings', to: '/admin/settings', icon: <GearIcon size={18} /> },
    ],
  },
]

/** Flattened leaves, for the ⌘K palette. */
export function navLeaves(sections: NavSection[]): Array<{ label: string; to: string; group: string }> {
  return sections.flatMap((section) =>
    section.items.flatMap((item) =>
      item.children
        ? item.children.map((child) => ({ label: child.label, to: child.to, group: item.label }))
        : [{ label: item.label, to: item.to, group: section.title ?? 'General' }],
    ),
  )
}
